import get from 'lodash/get';
import { registerPlugin } from '@capacitor/core';
import { isMobileVersion } from './platform';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export function getDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;

  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
    Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d * 1000;
}

const checkBrowser =
  typeof window.navigator !== 'undefined' && 'geolocation' in window.navigator;

export function getCurrentLocation() {
  let coordinates = {};

  return new Promise((resolve) => {
    if (checkBrowser && !isMobileVersion) {
      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          console.log({ coordinates });
          resolve(coordinates);
        },
        (error) => {
          console.error('Error getting current location:', error);
          resolve(coordinates);
        }
      );
    } else {
      BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'Cancel to prevent battery drain.',
          backgroundTitle: 'Tracking location',
          requestPermissions: true,
          stale: true,
          distanceFilter: 0,
        },
        (location) => {
          if (location) {
            coordinates = {
              lat: get(location, 'latitude'),
              lng: get(location, 'longitude'),
            };
          }
        }
      )
        .then((id) => {
          // eslint-disable-next-line func-names
          setTimeout(function () {
            resolve(coordinates);
            BackgroundGeolocation.removeWatcher({ id });
          }, 1000); // default to 1000
        })
        .catch((e) => {
          console.log('error on geo location');
          console.error(e);
          resolve(coordinates);
        });
    }
  });
}

export async function watchLocation(callback) {
  let waitId;

  if (checkBrowser && !isMobileVersion) {
    try {
      const successCallback = (position) => {
        const { latitude, longitude } = position.coords;
        callback({ lat: latitude, lng: longitude }, null);
      };

      const errorCallback = (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          // eslint-disable-next-line no-alert
          window.confirm(
            'Vemospay needs your location. Please allow location permission from the settings.'
          );
        }
        callback({}, error);
      };

      waitId = window.navigator.geolocation.watchPosition(
        successCallback,
        errorCallback
      );
    } catch (error) {
      console.error('Error while watching location:', error);
    }
  } else {
    try {
      waitId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'Cancel to prevent battery drain.',
          backgroundTitle: 'Tracking location',
          requestPermissions: true,
          stale: true,
          distanceFilter: 0,
        },
        (coordinates, err) => {
          if (err) {
            if (err.code === 'NOT_AUTHORIZED') {
              if (
                // eslint-disable-next-line no-alert
                window.confirm(
                  'Vemospay needs your location. Open settings now?'
                )
              ) {
                BackgroundGeolocation.openSettings();
              }
            }
            callback({}, err);
          } else {
            callback(
              {
                lat: get(coordinates, 'latitude'),
                lng: get(coordinates, 'longitude'),
              },
              null
            );
          }
        }
      );
    } catch (e) {
      // silently logs error
      console.error(e);
      console.error('watch error');
    }
  }

  return waitId;
}

export function clearWatch(waitId) {
  console.log('clearing watch', waitId);
  if (checkBrowser && !isMobileVersion) window.navigator.geolocation.clearWatch(waitId);
  else return BackgroundGeolocation.removeWatcher({ id: waitId });
}
